import { fromJS, isImmutable } from 'immutable';
import { findPropertiesByType } from '@pearsontechnology/aero-plugin-schema';
import findElementDataInScope from 'utils/scope/findElementDataInScope';
import { COMPONENT, PATHWAY } from './coursewareTypes';

// Get rid of all the graphql stuff "edges[].node", parse json strings, simplify objects, etc.
// Eventually some of this should be replaced with Apollo resolvers.
export const resolveWalkableResponse = (response) => {
  const { isPreventConfigAssetResolve = false, linkedDocumentItems } = response;
  const entryPoint = resolveEntryPoint(response);
  const schema = resolveSchema(response);
  const assets = resolveAssets(response);
  const rawConfig = unResolvedConfig(response);
  const config = isPreventConfigAssetResolve ? rawConfig : resolveConfig(response, assets, schema);
  const expandedConfig = resolveWalkableExpandedConfig(response, schema, config);
  const progress = resolveWalkableProgress(response);
  const pathways = resolveWalkablePathwayList(response);
  const ancestry = resolveAncestry(response);
  const theme = resolveTheme(response);
  const components = resolveComponents(response);
  const metadata = resolveMetaData(response);
  const scopeURN = resolveScopeURN(response);
  const scope = resolveScope(response);
  const plugin = resolvePlugin(response);

  return ({
    id: response.id,
    elementType: response.elementType,
    entryPoint,
    schema,
    config,
    expandedConfig,
    assets,
    linkedDocumentItems,
    progress,
    pathways,
    ancestry,
    theme,
    components,
    metadata,
    scopeURN,
    plugin,
    scope,
    rawConfig,
  });
};

export const resolveComponentResponse = (response, scopeURN) => {
  const assets = resolveAssets(response);
  const entryPoint = resolveEntryPoint(response);
  const schema = resolveSchema(response);
  const config = resolveConfig(response, assets, schema);
  const expandedConfig = resolveComponentExpandedConfig(response, config);
  const metadata = resolveMetaData(response);
  const plugin = resolvePlugin(response);
  const progress = {};
  const rawConfig = unResolvedConfig(response);

  return ({
    id: response.id,
    elementType: COMPONENT,
    scopeURN,
    assets,
    entryPoint,
    schema,
    config,
    expandedConfig,
    metadata,
    plugin,
    progress,
    rawConfig,
  });
};

export const resolveAncestry = (response) => {
  const ancestry = response.ancestry || [];
  return ancestry;
};

export const resolvePlugin = ({ plugin = {} }) => plugin;

export const resolveTheme = ({ theme }) => theme ? JSON.parse(theme) : undefined;

export const resolveScopeURN = ({ id, scope }) => findElementDataInScope(id, scope).scopeURN;

export const resolveScope = (response) => response.scope;

export const resolveSchema = ({
  plugin: {
    manifest: {
      configurationSchema = '{}',
    } = {},
  } = {},
}) => JSON.parse(configurationSchema);

export const resolveMetaData = ({
  coursewareMetaInfo = [],
}) => coursewareMetaInfo.reduce(
  (acc, { key, value }) => ({ ...acc, [key]: value }), {}
);

export const resolveAssets = ({ assets }) => assets?.edges?.map(removeNodes) || [];

export const resolveComponents = ({
  components = [],
}) => components.map((component) => ({
  ...component,
  assets: resolveAssets(component),
}));

export const resolveWalkablePathwayList = ({
  pathways,
}) => {
  const pathwaysFormatted = pathways || [];
  return pathwaysFormatted.map((pathway) => ({
    id: pathway.id,
    walkables: pathway?.walkables?.edges?.map(removeNodes) || [],
  }));
};

const isFullAssetDTOSchema = (schema) => schema.$schema !== undefined;

export const unResolvedConfig = ({ config = '{}' }) => JSON.parse(config);

export const resolveConfig = ({ config = '{}' }, assets, schema) => {
  const assetsJS = isImmutable(assets) ? assets.toJS() : assets;
  const URN_EXTERNAL = 'urn:external';

  const internalAssets = assetsJS.filter((asset) => !asset.urn.startsWith(URN_EXTERNAL));

  const replacements = internalAssets.map((asset) => ([
    `"${asset.urn}"`,
    isFullAssetDTOSchema(schema)
      ? JSON.stringify({ ...asset?.source, ...asset })
      : `"${asset.source?.url || asset.source?.original?.url}"`,
  ]));
  const replaced = replacements.reduce((doc,
    [original, replacement]) => doc.replace(new RegExp(original, 'g'), replacement),
  config);
  return JSON.parse(replaced);
};

export const resolveEntryPoint = ({
  plugin: {
    entryPoints = [{}],
  } = {},
}) => {
  const {
    contentType,
    entryPointPath: entryPoint,
    entryPointData,
  } = entryPoints.find((view) => view.context === 'LEARNER') || entryPoints[0];
  return {
    contentType,
    entryPoint,
    ...(contentType === 'application/embed' && { entryPointData }),
  };
};

export const resolveWalkableProgress = ({
  coursewareMetaInfo,
  attempt = {},
  attemptLimit,
  isInstructor,
  progress,
  score = {},
}) => {
  const { totalScore } = resolveMetaData({ coursewareMetaInfo });
  const scoredProgress = {
    attempt: { ...attempt, attemptLimit, isInstructor }, // In dashboard-lesson plugin, we've added a check to disable "Start" button. So the info. related to it passed in attempt object.
    score,
    completion: progress?.completion || {},
  };

  if (totalScore && totalScore.length > 0) {
    scoredProgress.totalScore = parseFloat(totalScore);
  }

  return scoredProgress;
};

export const resolveComponentExpandedConfig = (walkable, config) => fromJS(config);

export const resolveWalkableExpandedConfig = ({ pathways }, schema, config) => {
  let expandedConfig = fromJS(config);

  const pathwayProperties = findPropertiesByType(schema, PATHWAY);
  pathwayProperties.forEach((pathwayRef) => {
    const pathwayId = expandedConfig.getIn([...pathwayRef.path, 'pathwayId']);
    const { walkables } = pathways.find(
      (item) => (item.id === pathwayId)
    ) || {};
    if (walkables) {
      const { edges } = walkables || {};
      if (edges) {
        const pathwayRefs = edges
          .map((walkableItem) => {
            const { node } = walkableItem;
            if (node) {
              return { itemId: node.id };
            }
            return undefined;
          })
          .filter((item) => item);
        expandedConfig = expandedConfig.setIn([...pathwayRef.path], pathwayRefs);
      }
    } else {
      expandedConfig = expandedConfig.setIn([...pathwayRef.path], []);
    }
  });
  return expandedConfig;
};

const removeNodes = ({ node }) => node;
